import _ from 'lodash';
import { HEADLESS_ACTION_BOARD_LOADING_CLASS } from '@/actionBoards/actionBoard.constants';
import { Visualization } from '@/annotation/ckEditorPlugins/components/content.utilities.constants';
import { isAllContentFinishedLoading } from '@/annotation/contentSelector.utilities';
import { sqPluginStore, sqStateSynchronizer, sqWorkbookStore, sqWorksheetStore } from '@/core/core.stores';
import { PERSISTENCE_LEVELS } from '@/core/flux.service';
import { isSystemTest } from '@/core/utilities';
import { goTo, navigateToHeadlessRenderStandby } from '@/main/routing.utilities';
import { HEADLESS_CHART_RENDERING_CLASS } from '@/markdown/markdown.constants';
import { SCREENSHOT_SIZE_TO_CONTENT } from '@/reportEditor/report.constants';
import { getPendingRequestCount } from '@/requests/axios.utilities';
import * as pendingRequestsUtilities from '@/requests/pendingRequests.utilities';
import { HeadlessCategories, HeadlessJobFormat } from '@/services/headlessCapture.constants';
import {
  headlessCaptureMetadata,
  headlessRenderMode,
  setHeadlessCaptureMetadata,
} from '@/services/headlessCapture.utilities';
import { onAllServerRequestsCanceled } from '@/services/notifier.service';
import { HEADLESS_AG_RENDERING_CLASS } from '@/tableBuilder/tableBuilder.constants';
import { logError, logInfo } from '@/utilities/logger';
import { formatMessage } from '@/utilities/logger.utilities';
import { close as socketClose, waitForOpen } from '@/utilities/socket.utilities';
import { getStorageSafely } from '@/utilities/storage.utilities';
import { SeeqNames } from '@seeqdev/constants';
import type { VisualizationData } from '@/annotation/ckEditorPlugins/components/content.utilities.constants';
import type { HeadlessCategory } from '@/services/headlessCapture.constants';

/**
 * @file Requests and manages screenshots.
 *
 * This service has some functions which only run when the app is running in the headless browser used by
 *   NodeJS to capture screenshots. The 'callback' mechanism of that browser is used to send
 *   messages to NodeJS to inform it when the screenshot is ready to be taken.
 */

// Screenshot rendering only supports a small subset of paths within the application, if the headless browser is
// redirected to a page that isn't allowed the capture should fail. The load-error page is allowed because it has
// special handling for failing the headless capture with a relevant message.
export const ALLOWED_SCREENSHOT_MODE_PATHS = /^\/(present|headless-capture-standby|load-error|report-template)/;

// Warnings collected during the current capture, see notifyWarning()
let pendingWarnings: string[] = [];

/**
 * Tells headless capture browser that the app is ready to capture a screenshot.
 * This function does nothing unless the app is being run in the headless capture browser.
 *
 * @param getWarnings - optional; called once the app has settled to collect warnings to attach to the capture
 */
export function notifyCapture(getWarnings?: () => string[]) {
  function waitForQuiescence() {
    return Promise.all([waitForAppQuiescence(), waitForPluginQuiescence()]);
  }

  if (headlessRenderMode() || isSystemTest()) {
    // Wait for the app to settle then wait 100ms and wait for the app to settle again. This is an attempt to handle
    // views that request data asynchronously some (short) time after the view is first loaded.
    waitForQuiescence()
      .then(() => {
        return new Promise((resolve) => {
          setTimeout(resolve, 100);
        });
      })
      .then(waitForQuiescence)
      .then(() => {
        _.forEach(getWarnings?.(), notifyWarning);
        notifyCaptureReady();
      });
  }
}

/**
 * Sends any collected warnings and then tells the headless capture browser that the page is ready to be captured.
 */
export function notifyCaptureReady() {
  if (pendingWarnings.length > 0) {
    window.seeqHeadlessWarning?.(formatWarnings(pendingWarnings));
    pendingWarnings = [];
  }
  window.seeqHeadlessCapture?.();
  // @ts-ignore Used by playwright to know when page is settled
  window.isSeeqPageSettled = true;
}

/**
 * Tells the headless capture browser that the page is loading.
 * This function does nothing unless the app is being run in the headless capture browser.
 */
export function notifyLoading() {
  if (headlessRenderMode()) {
    window.seeqHeadlessLoading();
  }
}

/**
 * Tells the headless capture browser that the page encountered an unrecoverable error
 * This function does nothing unless the app is being run in the headless capture browser.
 */
export function notifyError(...errors: string[]) {
  if (headlessRenderMode()) {
    window.seeqHeadlessError(...errors);
  }
}

/**
 * Collects a warning to send with the capture. The headless capture browser only keeps the first warning it receives
 * for a capture, so warnings are sent together by {@link notifyCaptureReady}.
 * This function does nothing unless the app is being run in the headless capture browser.
 *
 * @param warning - the warning text, which may contain HTML
 */
export function notifyWarning(warning: string) {
  if (headlessRenderMode() && warning) {
    pendingWarnings.push(warning);
  }
}

/**
 * Formats the collected warnings as HTML, one line per distinct warning, each prefixed with a dash.
 */
function formatWarnings(warnings: string[]): string {
  return _.uniq(warnings)
    .map((warning) => `- ${warning}`)
    .join('<br>');
}

/**
 * Tells the headless capture browser that the currently-loading workbook has been cancelled by a user or
 * administrator.  This function does nothing unless the app is being run in the headless capture browser.
 */
export function notifyCancellation(...errors: string[]) {
  if (headlessRenderMode()) {
    window.seeqHeadlessCancellation(...errors);
  }
}

const DEFAULT_VISUALIZATION: VisualizationData = { visualization: Visualization.NONE };
// Exposed for testing
export const DEFAULT_GET_VISUALIZATION_DATA = () => Promise.resolve(DEFAULT_VISUALIZATION);

/**
 * Determines if visualization data has been provided by the interactive content since it will overwrite the
 * seeqGetVisualizationData function.
 * @exposedForTesting
 */
export function hasVisualizationDataBeenProvided(): boolean {
  return (
    headlessRenderMode() &&
    window.seeqGetVisualizationData !== undefined &&
    window.seeqGetVisualizationData !== DEFAULT_GET_VISUALIZATION_DATA
  );
}

/**
 * Attaches callbacks to window that puppeteer can call as well as a state change handler that closes the socket
 * and clears local storage. Does nothing if not in screenshot render mode. See custom-typings.d.ts (Window) for
 * more detailed description of the callbacks attached
 */
export function initializeHeadlessCaptureMode() {
  if (!headlessRenderMode()) {
    return;
  }

  window.seeqGetVisualizationData = DEFAULT_GET_VISUALIZATION_DATA;

  // Provides a hook that the headless capture browser can use to cancel all requests from this browser
  window.seeqInternalPageCleanup = () => {
    navigateToHeadlessRenderStandby();
    window.seeqGetVisualizationData = DEFAULT_GET_VISUALIZATION_DATA;
    pendingWarnings = [];
    logInfo("Cleaning up internal state via 'window.seeqInternalCleanup'");
    return (
      Promise.resolve(pendingRequestsUtilities.cancelAll())
        .catch((e) => logError(formatMessage`Error cancelling all requests: ${e}`))
        // Not all screenshots require the socket to be open, but the socket service will get into a bad state if
        // the socket is closed before it is fully established - to prevent this wait for the socket to be open before
        // capturing the screenshot
        .then(() => waitForOpen())
        .catch((e) => logError(formatMessage`Error waiting for socket to open: ${e}`))
        .then(() => {
          socketClose();
          getStorageSafely().clear();

          // Re-initialize all stores. This is necessary since some stores have special logic to retain certain
          /* information even when they are rehydrated; search for 'saveState' for examples. */ _.forEach(
            PERSISTENCE_LEVELS,
            (persistenceLevel) => {
              sqStateSynchronizer.initialize(persistenceLevel);
            },
          );
        })
        .catch((e) => logError(formatMessage`Error cleaning up page state: ${e}`))
    );
  };

  window.seeqNavigate = (url) => {
    if (!_.isString(url) || !_.startsWith(url, '/')) {
      throw new Error(`seeqNavigate: A relative url is required, but got '${url}'`);
    }
    if (url && !ALLOWED_SCREENSHOT_MODE_PATHS.test(url.replace(/^#!/, ''))) {
      notifyError(`Could not load path not allowed in screenshot render mode: ${url}`);
    }
    setTimeout(() => {
      goTo(url);
    }, 10);
  };

  onAllServerRequestsCanceled(() => {
    notifyError('Administrator canceled all requests');
  });
}

/**
 * Fetches the category of headless capture being requested so that it can be accessed synchronously in the page load
 */
export async function fetchHeadlessCaptureMetadata() {
  if (!headlessRenderMode()) {
    return Promise.resolve();
  }

  const [category, jobFormat] = await Promise.all([window.seeqHeadlessCategory(), window.seeqJobFormat()]);
  if (!isHeadlessCategory(category)) {
    throw new Error(`Error: '${category}' category is unexpected; the frontend enum likely needs to be updated`);
  }
  if (!isHeadlessJobFormat(jobFormat)) {
    throw new Error(`Error: '${jobFormat}' jobFormat is unexpected; the frontend enum likely needs to be updated`);
  }
  setHeadlessCaptureMetadata({ category, jobFormat });
}

/**
 * Test if {@param category} is a HeadlessCategory
 */
function isHeadlessCategory(category: string): category is HeadlessCategory {
  return HeadlessCategories.includes(category);
}

/**
 * Test if {@param jobFormat} is a HeadlessJobFormat
 */
function isHeadlessJobFormat(jobFormat: unknown): jobFormat is HeadlessJobFormat {
  return _.isString(jobFormat) && Object.values<string>(HeadlessJobFormat).includes(jobFormat);
}

/**
 * Determines if in headless mode and job is to return the props for a React component.
 */
export function isHeadlessReactJsonMode(): boolean {
  return headlessRenderMode() && headlessCaptureMetadata().jobFormat === HeadlessJobFormat.REACT_JSON;
}

/**
 * Determines if in headless mode and the job is to capture a worksheet thumbnail.
 */
export function isHeadlessThumbnailMode(): boolean {
  return headlessRenderMode() && headlessCaptureMetadata().category === SeeqNames.Origins.Thumbnail;
}

/**
 * Waits for Seeq to settle using the following checks. This isn't easy because not only does all the data need to
 * be fetched and React finish rendering, but other libraries, such as Highcharts and React need to finish resizing
 * and the browser finish painting. The following have to be true in order for the promise to resolve:
 * - no outstanding http requests
 * - all Topic content has finished loading (or errored).
 * - all ag-grid tables have finished rendering
 * - The timeout loop needs to finish in the expected time. If the setTimeout is not executing on time then the
 * event loop in the browser is still busy getting the page settled.
 *
 * @return Resolves when the app has stabilized
 */
export function waitForAppQuiescence(): Promise<void> {
  const TIMEOUT_MILLIS = 100;
  // Allow for a small amount of drift. These numbers based on watching actual numbers on heavy documents
  const TIMEOUT_DURATION_MIN = 90;
  const TIMEOUT_DURATION_MAX = 120;
  const shouldRequireOnTimeEventLoop = headlessRenderMode();
  return new Promise<void>((resolve) => waitUntilSeeqSettled(resolve, performance.now()));

  function waitUntilSeeqSettled(resolve: () => void, lastRunTime: number) {
    const currentRunTime = performance.now();
    const actualTimeoutDuration = currentRunTime - lastRunTime;
    const isEventLoopOnTime =
      actualTimeoutDuration >= TIMEOUT_DURATION_MIN && actualTimeoutDuration <= TIMEOUT_DURATION_MAX;
    const isStableEventLoop = !shouldRequireOnTimeEventLoop || isEventLoopOnTime;
    if (
      getPendingRequestCount() === 0 &&
      ((isAllContentFinishedLoading() &&
        areAllAgGridsFinished() &&
        areAllChartsFinished() &&
        areAllActionBoardsFinished() &&
        areAllImagesLoaded()) ||
        hasVisualizationDataBeenProvided()) &&
      isStableEventLoop
    ) {
      setTimeout(
        () => {
          removeHeightFromCKTables();
          updateAgGridSizing();
          resolve();
        },
        !!getAgGridElement() && headlessCaptureMetadata().jobFormat === HeadlessJobFormat.PNG ? 100 : 1,
      );
      return;
    }
    setTimeout(() => waitUntilSeeqSettled(resolve, currentRunTime), TIMEOUT_MILLIS);
  }
}

/**
 * Determines if all img tags have finished loading. Needed for creating PDFs of topics. Also handles removing the
 * loading attribute from images that are configured to load lazily so that images below the fold are also loaded.
 */
function areAllImagesLoaded() {
  const imagesNotLoaded = Array.from(document.querySelectorAll('img')).filter(
    (el) => window.getComputedStyle(el).display !== 'none' && !el.complete,
  );
  imagesNotLoaded.forEach((el) => {
    el.removeAttribute('loading');
  });
  return imagesNotLoaded.length === 0;
}

function areAllAgGridsFinished() {
  const tableSelector = document.querySelector(SCREENSHOT_SIZE_TO_CONTENT.SELECTOR);
  // This function gets called when in headless render mode and when rendering the page in a system test. We only
  // need to care about this in headless render mode though, so if not in headless render mode, just return true.
  const isTableSelectorPresent = headlessRenderMode() ? sqWorksheetStore.view.key !== 'TABLE' || !!tableSelector : true;

  return document!.querySelectorAll(`.${HEADLESS_AG_RENDERING_CLASS}`).length === 0 && isTableSelectorPresent;
}

/**
 * Determines if all markdown charts (using Highcharts) have finished rendering.
 * Used in headless capture mode to ensure charts are fully loaded before PDF generation.
 * Charts add the HEADLESS_CHART_RENDERING_CLASS when mounting and remove it when the
 * Highcharts 'load' event fires.
 *
 * @returns {boolean} true if no charts are currently rendering
 */
function areAllChartsFinished(): boolean {
  if (!document) {
    return true; // Defensive: if no document, don't block
  }
  return document.querySelectorAll(`.${HEADLESS_CHART_RENDERING_CLASS}`).length === 0;
}

function areAllActionBoardsFinished(): boolean {
  const hasLoadingState = !!document.querySelector(`.${HEADLESS_ACTION_BOARD_LOADING_CLASS}`);
  if (hasLoadingState || !sqWorkbookStore.isActionBoard) {
    return !hasLoadingState;
  }

  const actionBoard = document.querySelector<HTMLElement>('[data-testid="actionBoard"]');
  const grid = actionBoard?.querySelector<HTMLElement>('.react-grid-layout');
  const tiles = Array.from(actionBoard?.querySelectorAll<HTMLElement>('.actionBoardTile') ?? []);
  return hasSize(actionBoard) && (!grid || (hasSize(grid) && tiles.length > 0 && tiles.every(hasSize)));
}

function hasSize(element?: HTMLElement | null): element is HTMLElement {
  const rect = element?.getBoundingClientRect();
  return !!rect?.width && !!rect?.height;
}

/**
 * Ag-Grid has a variety of resizing options which can be difficult to manage. Due to us using our own custom
 * styling on top of the table, when grouping and using the domLayout 'print' mode, the groups sometimes will get
 * smooshed together. To work around this, we use the `autoHeight` mode and suppress row virtualization, forcing all
 * of the rows to be rendered. This causes another problem wherein Ag-Grid takes up the full width of the screen
 * rather than just the table width, due to differences in styling between 'print' and 'autoHeight'. Working around
 * that inside the table builder component requires us to set and unset the width as it resizes multiple times on
 * load, which over complicates the component. Instead, we can wait until everything has settled and then update the
 * width once, right here.
 */
function updateAgGridSizing() {
  if (!sqWorkbookStore.isTopic && headlessRenderMode()) {
    const maybeAgGrid = getAgGridElement();
    if (maybeAgGrid) {
      // Headers might be disabled but there might be no rows at all so just take the minimum of all possible values
      const viewportWidth =
        (maybeAgGrid.querySelector('.ag-header-viewport') as HTMLElement)?.offsetWidth ?? Number.MAX_SAFE_INTEGER;
      const containerWidth =
        (maybeAgGrid.querySelector('.ag-header-container') as HTMLElement)?.offsetWidth ?? Number.MAX_SAFE_INTEGER;
      const rowWidth =
        (maybeAgGrid.querySelector('.ag-center-cols-container') as HTMLElement)?.offsetWidth ?? Number.MAX_SAFE_INTEGER;

      const newWidth = Math.min(viewportWidth, containerWidth, rowWidth);
      maybeAgGrid.style.width = `${newWidth}px`;
    }
  }
}

function getAgGridElement(): HTMLElement | null {
  return document.querySelector('.ag-root-wrapper');
}

/**
 * CKEditor tables that have a manually set height cause problems when exporting to PDF because the table
 * height is treated differently due to the various PDF styles that get enabled. We can remove the height and use
 * the natural height of the element instead.
 */
function removeHeightFromCKTables() {
  document.querySelectorAll('figure.table.ck-widget').forEach((el) => {
    if ((el as HTMLElement)?.style?.height) {
      (el as HTMLElement).style.height = '';
    }
  });
}

/**
 * Waits for any visible display pan plugins to complete their rendering
 *
 * @return {Promise} that resolves when all visible display pane plugins have completed their rendering
 */
export function waitForPluginQuiescence(): Promise<void> {
  return new Promise<void>((resolve) => waitUntilPluginRenderComplete(resolve));

  function waitUntilPluginRenderComplete(resolve: () => void) {
    const keepWaiting = () => setTimeout(_.partial(waitUntilPluginRenderComplete, resolve), 200);

    if (sqPluginStore.displayPaneRenderComplete) {
      setTimeout(resolve, 1);
    } else {
      keepWaiting();
    }
  }
}
